import Image from 'next/image';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { CLIENT_FORM } from '@common/schema/flow';
import { useSearchCityPriceQuery } from '@controller/schema';
import { cx } from '@emotion/css';

interface Props {
  step: number;
  stepCount: number;
  onNext: (step: number) => void;
  styles: any;
}

const DAY_RATE_IDS = [10, 13, 24, 25, 26, 27, 28, 29, 30, 46, 47];

const HourlyPrice = ({ styles, price, icon, flag }: any) => {
  return (
    <div className={styles.pricing_blk}>
      <div className={styles.pricing_icon_wrpr}>
        <Image
          src={`/images/icons/${icon}`}
          alt="pricing icon"
          className={styles.pricing_icon}
          height={50}
          width={50}
        />
      </div>
      <div className={cx(styles.pricing_txt, styles.text_center)}>
        <h5 className={cx(styles.h5, styles.pricing_head)}>How we charge?</h5>
        <p className={cx(styles.p, styles.pricing_desc)}>
          We charge a minimum of 1 hour labour
        </p>
        <h5 className={cx(styles.h5, styles.price)}>
          £ {price} + VAT {flag && ' per hour'}
        </h5>
        <p className={cx(styles.p, styles.pricing_desc, styles.m_0)}>
          After the first hour, charges will apply in 30 minute increments. If
          parts / materials are required these will be an additional cost.
        </p>
      </div>
    </div>
  );
};

const FixedPricing = ({ styles, price, icon }: any) => {
  return (
    <div className={styles.pricing_blk}>
      <div className={styles.pricing_icon_wrpr}>
        <Image
          src={`/images/icons/${icon}`}
          alt="pricing icon"
          className={styles.pricing_icon}
          height={50}
          width={50}
        />
      </div>
      <div className={cx(styles.pricing_txt, styles.text_center)}>
        <h5 className={cx(styles.h5, styles.pricing_head)}>How we charge?</h5>
        <p className={cx(styles.p, styles.pricing_desc)}>
          We charge a fixed price for this service.
        </p>
        <h5 className={cx(styles.h5, styles.price)}>£ {price} + VAT</h5>
        <p className={cx(styles.p, styles.pricing_desc, styles.m_0)}>
          If any additional work, repairs, or parts / materials are required, we
          will provide a quotation before carrying out any further work.
        </p>
      </div>
    </div>
  );
};

const conditionForFixedPricing = (subProblemTypeId: any) => {
  if (
    subProblemTypeId == 10 ||
    subProblemTypeId == 13 ||
    subProblemTypeId == 14 ||
    (subProblemTypeId >= 24 && subProblemTypeId <= 30) ||
    (subProblemTypeId >= 46 && subProblemTypeId <= 48)
  ) {
    return true;
  }
  return false;
};
export const Pricing = (props: Props) => {
  const { step, stepCount, styles } = props;
  const { watch, setValue } = useFormContext();

  const city = watch(CLIENT_FORM.CITY);
  const service = watch(CLIENT_FORM.PROBLEM_CATEGORY_ID);

  const subProblemType = watch(CLIENT_FORM.PROBLEM_SUB_CATEGORY_ID);
  const { data } = useSearchCityPriceQuery(
    {
      input: {
        cityId: JSON.parse(city).id,
        problemSubcategoryId: JSON.parse(subProblemType).id,
      },
    },
    { enabled: !!city && !!service && !!subProblemType },
  );

  useEffect(() => {
    if (data && data?.searchCityPrice?.cityPriceList) {
      const price = data?.searchCityPrice?.cityPriceList[0].price;
      setValue(CLIENT_FORM.CLIENT_COST, price);
    }
  }, [data]);

  const onNext = () => {
    props.onNext(stepCount);
  };

  const cityPriceList = data?.searchCityPrice?.cityPriceList;
  let price = 0;
  if (
    !service ||
    !subProblemType ||
    !cityPriceList ||
    cityPriceList?.length === 0
  ) {
    price = 0;
  } else {
    price = cityPriceList[0].price ?? 0;
  }
  let icon = '';
  if (service && JSON.parse(service)) {
    icon = JSON.parse(service).iconName;
  }

  let flag = true;
  if (subProblemType) {
    const id = JSON.parse(subProblemType).id;
    if (DAY_RATE_IDS.includes(id)) {
      flag = false;
    }
  }
  return (
    <div
      className={cx(styles.f_wrpr, {
        [styles.active]: step >= stepCount,
      })}>
      <label className={styles.f_label}>Pricing</label>
      {conditionForFixedPricing(JSON.parse(subProblemType).id) ? (
        <FixedPricing styles={styles} price={price} icon={icon} />
      ) : (
        <HourlyPrice styles={styles} price={price} icon={icon} flag={flag} />
      )}
      <div className={cx(styles.f_btn, styles.text_center)}>
        <button
          className={cx(styles.btn_accent, styles.form_btn)}
          onClick={onNext}>
          Continue
        </button>
      </div>
    </div>
  );
};
